import Cookies from 'js-cookie';
import jwt, { JwtPayload } from 'jsonwebtoken';
import { Dispatch, SetStateAction } from 'react';

import { JwtTokens } from '@/graphql/API';
import { User } from '@/graphql/types/user';
import { TokenData } from '@/types/auth';

const AUTH_TOKEN_COOKIE = 'auth_token';
const REFRESH_TOKEN_COOKIE = 'refresh_token';
const cookiesOptions = { secure: true, sameSite: 'strict', path: '/' };

export const extractTokenData = (token: string): TokenData => {
  const decoded = jwt.decode(token) as JwtPayload;
  const expiresAt = decoded.exp ? new Date(decoded.exp * 1000) : null;
  const timeRemaining = decoded.exp
    ? Math.max(0, Math.floor((decoded.exp * 1000 - Date.now()) / 1000))
    : 0;
  const userId = decoded.userId || null;

  return { expiresAt, timeRemaining, userId };
};

export const getExpirationInDays = (expiresAt: Date) =>
  (new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24);

export const setTokenCookies = (tokens: JwtTokens) => {
  const accessTokenData = extractTokenData(tokens.accessToken);
  const refreshTokenData = extractTokenData(tokens.refreshToken);

  if (!accessTokenData.expiresAt || !refreshTokenData.expiresAt) {
    console.error('Invalid token expiration times');
    return;
  }

  const accessTokenExpiresInDays = getExpirationInDays(
    accessTokenData.expiresAt
  );
  const refreshTokenExpiresInDays = getExpirationInDays(
    refreshTokenData.expiresAt
  );

  Cookies.set(AUTH_TOKEN_COOKIE, tokens.accessToken, {
    expires: accessTokenExpiresInDays,
    ...cookiesOptions
  });

  Cookies.set(REFRESH_TOKEN_COOKIE, tokens.refreshToken, {
    expires: refreshTokenExpiresInDays,
    ...cookiesOptions
  });
};

export const clearTokenCookies = () => {
  Cookies.remove(AUTH_TOKEN_COOKIE, { path: '/' });
  Cookies.remove(REFRESH_TOKEN_COOKIE, { path: '/' });
};

export const getTokensFromCookies = (): JwtTokens | null => {
  const accessToken = Cookies.get(AUTH_TOKEN_COOKIE) || '';
  const refreshToken = Cookies.get(REFRESH_TOKEN_COOKIE) || '';

  if (!accessToken || !refreshToken) {
    return null;
  }

  return {
    accessToken,
    refreshToken
  };
};

export const handleAuthState = ({
  user,
  isAuthenticated,
  isAuthReady,
  setIsAuthenticated,
  setIsAuthReady
}: {
  user: User | null;
  isAuthenticated: boolean;
  isAuthReady: boolean;
  setIsAuthenticated: Dispatch<SetStateAction<boolean>>;
  setIsAuthReady: Dispatch<SetStateAction<boolean>>;
}) => {
  if (!user && isAuthenticated) {
    setIsAuthenticated(false);
    return;
  }

  if (
    !isAuthReady &&
    ((user && isAuthenticated) || (!user && !isAuthenticated))
  ) {
    // Todo: check if needed timeout
    setTimeout(() => {
      setIsAuthReady(true);
    }, 10);
  }
};
